/**
 * Talk BI chart components (spec §7, the approved comp). Props are plain data; nothing fetches.
 * Each draws on a Chart.js canvas, so Chart.getChart(canvas).config.type stays checkable.
 * `format` is a bi-format kind: 'money' | 'qty' | 'pct' | 'share'.
 */
import {
  formatter, toNumber, sliceColors, timeAxis, theme, shareOf, restNote, summary,
  rankedConfig, trendConfig, donutConfig, stackedConfig,
} from './bi-charts.mjs';
import { share } from './bi-format.mjs';

const { useRef, useEffect } = React;
const FONT = "'Heebo', Arial, sans-serif";
const readVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name);

function textWidth(texts, font) {
  const ctx = document.createElement('canvas').getContext('2d');
  ctx.font = font;
  return Math.ceil(Math.max(0, ...texts.map((t) => ctx.measureText(t).width)));
}

// The one canvas every visual draws on. It rebuilds only when the data key changes (as ChatChart
// keys on its spec JSON), so an equal re-render of the parent never reconstructs the chart.
function BiCanvas({ build, dataKey, label, height, lang, children }) {
  const ref = useRef(null);
  const buildRef = useRef(build);
  buildRef.current = build;
  useEffect(() => {
    if (!ref.current || !window.Chart) return undefined;
    const chart = new window.Chart(ref.current, buildRef.current(theme(readVar)));
    return () => chart.destroy();
  }, [dataKey]);
  if (!window.Chart) return <p className="chat-figure-err">{lang === 'he' ? 'ספריית התרשימים לא נטענה' : 'The chart library did not load'}</p>;
  return (
    <div className="bi-plot" style={{ height }}>
      <canvas ref={ref} role="img" aria-label={label}>{label}</canvas>
      {children}
    </div>
  );
}

export function RankedBars({ rows, label, value, format = 'qty', rest = null, lang, height }) {
  const rtl = lang === 'he';
  const labels = rows.map((r) => String(r[label] ?? ''));
  const values = rows.map((r) => toNumber(r[value]));
  const note = restNote(rest, values, format, lang);
  const build = (T) => rankedConfig({
    labels, values, kind: format, rtl, theme: T,
    labelWidth: textWidth(values.filter(Number.isFinite).map(formatter(format)), `500 12px ${FONT}`) + 10,
  });
  return (
    <div className="bi-chart">
      <BiCanvas build={build} dataKey={JSON.stringify([labels, values, format, lang])} lang={lang}
        height={height ?? Math.max(120, rows.length * 34 + 16)}
        label={summary(labels.map((l, i) => [l, values[i]]), format, lang)}/>
      {note && <p className="bi-foot">{note}</p>}
    </div>
  );
}

// points / comparePoints: [{x, y}], aligned by index; x is a month (1-12) or an ISO date.
export function TrendLine({ points, comparePoints = null, format = 'qty', reference = null, names, lang, height = 280 }) {
  const rtl = lang === 'he';
  const axis = timeAxis(points.map((p) => p.x), lang);
  const values = points.map((p) => toNumber(p.y));
  const compare = comparePoints ? points.map((_, i) => toNumber(comparePoints[i]?.y)) : null;
  const build = (T) => trendConfig({ ...axis, values, compare, names, kind: format, rtl, theme: T, reference });
  return (
    <div className="bi-chart">
      <BiCanvas build={build} dataKey={JSON.stringify([axis, values, compare, names, format, reference, lang])} lang={lang}
        height={height} label={summary(axis.titles.map((t, i) => [t, values[i]]), format, lang)}/>
    </div>
  );
}

// slices: [{key, label, value, other?}]; `other` is the rest bucket (grey, never pickable).
// order: the entities' stable order, so a slice keeps its hue when filtering re-ranks them.
// onPick(key) toggles: picking the selected slice again sends null.
export function Donut({ slices, format = 'qty', total, onPick, selected = null, order, lang, height = 180 }) {
  const rtl = lang === 'he';
  const values = slices.map((s) => toNumber(s.value));
  const sum = total ?? values.reduce((a, v) => a + (v || 0), 0);
  const keys = order ?? slices.filter((s) => !s.other).map((s) => s.key);
  const swatches = sliceColors(slices, { order: keys });
  const colors = sliceColors(slices, { order: keys, selected });
  const short = formatter(format);
  const pickRef = useRef(null);
  pickRef.current = (i) => {
    const s = slices[i];
    if (onPick && s && !s.other) onPick(s.key === selected ? null : s.key);
  };
  const build = (T) => donutConfig({
    labels: slices.map((s) => s.label), values, colors, kind: format, rtl, theme: T, onPick: (i) => pickRef.current(i),
  });
  return (
    <div className="bi-chart">
      <BiCanvas build={build} dataKey={JSON.stringify([slices, colors, format, lang])} lang={lang} height={height}
        label={summary(slices.map((s, i) => [s.label, values[i]]), format, lang)}>
        <div className="bi-donut-center">
          <span className="bi-donut-total" dir="ltr">{short(sum)}</span>
          <span className="bi-donut-caption">{rtl ? 'סה״כ' : 'Total'}</span>
        </div>
      </BiCanvas>
      <ul className="bi-legend">
        {slices.map((s, i) => {
          const body = <>
            <span className="bi-swatch" style={{ background: swatches[i] }} aria-hidden="true"/>
            <span className="bi-legend-name"><bdi>{s.label}</bdi></span>
            <span className="bi-legend-value" dir="ltr">{short(values[i])}</span>
            <span className="bi-legend-share">{share(shareOf(values[i], sum))}</span>
          </>;
          return (
            <li key={s.other ? `other-${i}` : String(s.key)}>
              {onPick && !s.other
                ? <button type="button" className="bi-legend-row" aria-pressed={selected === s.key} onClick={() => pickRef.current(i)}>{body}</button>
                : <span className="bi-legend-row">{body}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// rows: [{[x]: category, [seriesKey]: value, ...}]; series: the value keys, in their stable order.
export function StackedBars({ rows, x, series, format = 'qty', order, lang, height = 280 }) {
  const rtl = lang === 'he';
  const axis = timeAxis(rows.map((r) => r[x] ?? ''), lang);
  const colors = sliceColors(series.map((key) => ({ key })), { order: order ?? series });
  const sets = series.map((key, i) => ({ name: String(key), values: rows.map((r) => toNumber(r[key])), color: colors[i] }));
  const totals = rows.map((_, ri) => sets.reduce((a, s) => a + (s.values[ri] || 0), 0));
  const build = (T) => stackedConfig({ labels: axis.ticks, titles: axis.titles, series: sets, kind: format, rtl, theme: T });
  return (
    <div className="bi-chart">
      <BiCanvas build={build} dataKey={JSON.stringify([axis, sets, format, lang])} lang={lang} height={height}
        label={summary(axis.titles.map((t, i) => [t, totals[i]]), format, lang)}/>
    </div>
  );
}
